// src/pages/AgentPage/index.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Bot,
  Lock,
  Send,
  CheckCircle2,
  RotateCcw,
  FolderOpen,
  Download,
  Trash2,
  Globe,
  Square,
} from 'lucide-react';
import { ChatMarkdown } from 'components/ChatMarkdown';
import { formatChatMessageTime } from 'utils/dateUtils';
import type { DamFile } from 'mocks/dam';
import {
  listDamFolders,
  listDamFolderFiles,
  getDamFileContent,
  deleteCreatedFile,
} from 'services/demoApi/damApi';
import { deleteUploadedFile } from 'services/demoApi/damUploads';
import { downloadDamFile } from 'services/demoApi/fileDownload';
import { contentToPlainText } from 'mocks/damContents';
import { detectStreamingFile, hideStreamingMarkerTail } from 'services/demoApi/agentFileMarker';
import type { AgentContextFile } from 'services/demoApi/promptDecorators';
import { useAgentChatSession } from 'hooks/useAgentChatSession';
import { getActiveModel } from 'services/demoApi/modelsAdminApi';
import ObfuscationIndicator from 'components/ObfuscationIndicator';
import { FileExtIcon } from 'pages/DataSourcesPage/fileIcons';
import AgentSteps, { buildAgentSteps } from './AgentSteps';
import { MessageFileAttachment, FileGeneratingIndicator } from './MessageFileAttachment';

type FolderOption = { id: string; name: string; fileCount: number };

const MSG_AI = {
  bgcolor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '12px 12px 12px 4px',
  color: 'var(--color-text-secondary)',
};
const MSG_USER = {
  bgcolor: 'rgba(59,139,255,0.08)',
  border: '1px solid var(--color-primary-light)',
  borderRadius: '12px 12px 4px 12px',
  color: 'var(--color-text-primary)',
};

export default function AgentPage() {
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [folderId, setFolderId] = useState('');
  const [files, setFiles] = useState<DamFile[]>([]);
  const [text, setText] = useState('');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Активна ли сейчас внешняя модель — меняет плашку контура (замкнутый ↔ обфускация)
  const [externalModel, setExternalModel] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    displayedContent,
    isLoading,
    obfuscating,
    error,
    createdFile,
    sendMessage,
    stopGeneration,
    startNewDialog,
  } = useAgentChatSession();

  useEffect(() => {
    let cancelled = false;
    listDamFolders().then((items) => {
      if (cancelled) return;
      setFolders(items);
      if (items.length > 0) setFolderId(items[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!folderId) return;
    let cancelled = false;
    listDamFolderFiles(folderId).then((items) => {
      if (cancelled) return;
      setFiles(items);
    });
    return () => {
      cancelled = true;
    };
  }, [folderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, displayedContent]);

  // Тип активной модели: на монтировании и при каждом запросе (модель могли сменить в админке)
  useEffect(() => {
    let cancelled = false;
    getActiveModel().then((model) => {
      if (!cancelled) setExternalModel(!!model?.external);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  // FR-3.2: созданный агентом файл появляется в панели с подсветкой
  useEffect(() => {
    if (!createdFile || createdFile.folderId !== folderId) return;
    let cancelled = false;
    listDamFolderFiles(folderId).then((items) => {
      if (cancelled) return;
      setFiles(items);
    });
    setHighlightId(createdFile.id);
    const timer = setTimeout(() => setHighlightId(null), 4000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [createdFile, folderId]);

  const folder = folders.find((item) => item.id === folderId);
  const steps = useMemo(
    () =>
      buildAgentSteps(
        folder?.name ?? '',
        files.map((file) => file.name)
      ),
    [folder?.name, files]
  );
  // Шаги показываем пока генерация не началась (стриминг пуст) и не идёт обфускация
  const stepsActive = isLoading && !obfuscating && !displayedContent;
  // Пока идёт обфускация — прячем уже пришедший ответ ИИ, чтобы он не всплыл раньше времени
  const displayMessages =
    obfuscating && messages.length > 0 && messages[messages.length - 1].role === 'AI'
      ? messages.slice(0, -1)
      : messages;

  // В стриминге маркер CREATE_FILE скрыт из текста — показываем вместо него
  // карточку «Генерирую файл…» (имя/расширение появляются, когда тег достримился)
  const generatingFile = isLoading && !obfuscating ? detectStreamingFile(displayedContent) : null;

  const sendDisabled = isLoading || !folder;

  const send = async () => {
    if (!text.trim() || sendDisabled) return;
    const message = text.trim();
    setText('');
    const contextFiles: AgentContextFile[] = await Promise.all(
      files.map(async (file) => {
        const content = await getDamFileContent(file.id);
        return { name: file.name, contentText: content ? contentToPlainText(content) : null };
      })
    );
    await sendMessage(message, { id: folder.id, name: folder.name }, contextFiles);
  };

  const handleDeleteFile = async (file: DamFile) => {
    setDeletingId(file.id);
    try {
      if (file.docType === 'Загруженный' || file.id.startsWith('upload-')) {
        await deleteUploadedFile(file.id);
      } else {
        await deleteCreatedFile(file.id);
      }
      // EC-13: перезагрузить список — удалённый файл не попадёт в контекст следующих сообщений
      if (folderId) {
        const items = await listDamFolderFiles(folderId);
        setFiles(items);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const cardSx = {
    background: '#FFFFFF',
    border: '1px solid var(--color-border)',
    boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
    borderRadius: 'var(--radius-lg)',
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: '24px 48px' },
        maxWidth: 1600,
        mx: 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(135deg, #0A45C0, #1474FF)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Bot size={19} color="#fff" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.4px' }}>
              ИИ-ассистент
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
            Автономно работает с выбранной папкой документов в замкнутом контуре
          </Typography>
        </Box>
        <Button
          startIcon={<RotateCcw size={14} />}
          onClick={startNewDialog}
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
          }}
        >
          Новый диалог
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Левая панель: рабочая папка (FR-4.1) */}
        <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--color-border)' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25 }}>
              Рабочая папка агента
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={folderId}
              disabled={isLoading}
              onChange={(e) => setFolderId(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 13,
                  '& fieldset': { borderColor: 'var(--color-border)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', mr: 1, color: 'var(--color-primary)' }}>
                    <FolderOpen size={14} />
                  </Box>
                ),
              }}
            >
              {folders.map((item) => (
                <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                  {item.name} ({item.fileCount})
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Список файлов со статусом индексации */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
            {files.map((file) => (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.9,
                  transition: 'background 0.5s',
                  bgcolor: highlightId === file.id ? 'rgba(5,150,105,0.12)' : 'transparent',
                }}
              >
                <FileExtIcon ext={file.ext} size={13} />
                <Tooltip title={file.name} placement="top" enterDelay={300}>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12.5,
                      color: 'var(--color-text-secondary)',
                    }}
                    noWrap
                  >
                    {file.name}
                  </Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <CheckCircle2 size={12} style={{ color: '#059669' }} />
                  <Typography sx={{ fontSize: 10.5, color: '#059669', fontWeight: 600 }}>
                    {highlightId === file.id ? 'Создан агентом' : 'Проиндексировано'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    aria-label={`Скачать ${file.name}`}
                    disabled={downloadingId === file.id}
                    onClick={() => {
                      setDownloadingId(file.id);
                      downloadDamFile(file)
                        .catch(() => undefined)
                        .finally(() => setDownloadingId(null));
                    }}
                    sx={{
                      p: 0.5,
                      color: 'var(--color-text-muted)',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    {downloadingId === file.id ? (
                      <CircularProgress size={12} />
                    ) : (
                      <Download size={13} />
                    )}
                  </IconButton>
                  {(file.docType === 'Сгенерированный' || file.docType === 'Загруженный') && (
                    <IconButton
                      size="small"
                      aria-label="Удалить файл"
                      disabled={deletingId === file.id}
                      onClick={() => void handleDeleteFile(file)}
                      sx={{
                        p: 0.5,
                        color: 'var(--color-text-muted)',
                        '&:hover': { color: '#dc2626' },
                      }}
                    >
                      {deletingId === file.id ? (
                        <CircularProgress size={12} />
                      ) : (
                        <Trash2 size={13} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Плашка контура: замкнутый (внутренняя модель) ↔ обфускация (внешняя модель) */}
          {externalModel ? (
            <Box
              sx={{
                m: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: 'var(--radius-md)',
                bgcolor: 'rgba(20,116,255,0.06)',
                border: '1px solid rgba(20,116,255,0.22)',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <Globe
                size={14}
                style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: 1 }}
              />
              <Typography sx={{ fontSize: 11.5, color: '#0F5FE0', lineHeight: 1.45 }}>
                Активна внешняя модель: запросы отправляются внешнему провайдеру. Конфиденциальные
                данные обезличиваются (обфускация) при отправке.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                m: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: 'var(--radius-md)',
                bgcolor: 'rgba(5,150,105,0.07)',
                border: '1px solid rgba(5,150,105,0.22)',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <Lock size={14} style={{ color: '#059669', flexShrink: 0, marginTop: 1 }} />
              <Typography sx={{ fontSize: 11.5, color: '#047857', lineHeight: 1.45 }}>
                Замкнутый контур: агент работает только с файлами выбранной папки. Данные не
                покидают периметр компании.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Правая панель: чат (FR-4.2) */}
        <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            {messages.length === 0 && !isLoading && (
              <Box
                sx={{
                  m: 'auto',
                  textAlign: 'center',
                  color: 'var(--color-text-muted)',
                  maxWidth: 420,
                }}
              >
                <Bot size={30} style={{ opacity: 0.4 }} />
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    mt: 1,
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Задайте вопрос по документам папки «{folder?.name ?? '…'}»
                </Typography>
                <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                  Например: «Сделай краткую выжимку по всем документам» или «Какие риски упоминаются
                  в этих файлах?»
                </Typography>
              </Box>
            )}

            {displayMessages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  flexDirection: msg.role === 'User' ? 'row-reverse' : 'row',
                }}
              >
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    maxWidth: '78%',
                    minWidth: 0,
                    overflowX: 'auto',
                    fontSize: 13,
                    lineHeight: 1.55,
                    ...(msg.role === 'AI' ? MSG_AI : MSG_USER),
                  }}
                >
                  <ChatMarkdown content={msg.content} />
                  {msg.role === 'AI' && msg.attachedFile && (
                    <MessageFileAttachment attachment={msg.attachedFile} />
                  )}
                  {msg.created_at && (
                    <Box
                      sx={{
                        mt: 0.5,
                        fontSize: 10,
                        opacity: 0.6,
                        textAlign: 'right',
                        userSelect: 'none',
                      }}
                    >
                      {formatChatMessageTime(msg.created_at)}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            {(isLoading || obfuscating) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    ...MSG_AI,
                    px: 1.75,
                    py: 1.25,
                    maxWidth: '78%',
                    fontSize: 13,
                    lineHeight: 1.55,
                  }}
                >
                  {obfuscating ? (
                    <ObfuscationIndicator />
                  ) : displayedContent ? (
                    <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
                      <ChatMarkdown content={hideStreamingMarkerTail(displayedContent)} />
                      {generatingFile && <FileGeneratingIndicator info={generatingFile} />}
                    </Box>
                  ) : (
                    <AgentSteps steps={steps} active={stepsActive} />
                  )}
                </Box>
              </Box>
            )}

            {error && (
              <Box
                sx={{
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 12.5,
                  bgcolor: 'rgba(220,38,38,0.06)',
                  border: '1px solid rgba(220,38,38,0.25)',
                  color: '#dc2626',
                }}
              >
                {error}
              </Box>
            )}

            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              gap: 1.25,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="textarea"
              value={text}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Спросите агента о документах папки «${folder?.name ?? '…'}»…`}
              rows={1}
              sx={{
                flex: 1,
                resize: 'none',
                border: '1.5px solid',
                borderColor: 'var(--color-border)',
                borderRadius: 'var(--radius-md)',
                px: 1.5,
                py: 1,
                fontSize: 13,
                fontFamily: 'inherit',
                color: 'var(--color-text-primary)',
                bgcolor: '#f9fafb',
                outline: 'none',
                minHeight: 36,
                maxHeight: 120,
                overflowY: 'auto',
                transition: 'all 0.15s',
                '&:focus': { borderColor: 'var(--color-primary)', bgcolor: 'var(--color-surface)' },
              }}
            />
            {isLoading ? (
              <Box
                component="button"
                type="button"
                aria-label="Остановить генерацию"
                title="Остановить генерацию"
                onClick={() => void stopGeneration()}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  padding: 0,
                  bgcolor: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: '#dc2626' },
                }}
              >
                <Square size={13} color="#fff" fill="#fff" />
              </Box>
            ) : (
              <Box
                component="button"
                aria-label="Отправить сообщение агенту"
                onClick={() => void send()}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  padding: 0,
                  bgcolor: sendDisabled ? '#e5e7eb' : 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: sendDisabled ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                  '&:hover': sendDisabled ? {} : { bgcolor: 'var(--color-primary-dark)' },
                }}
              >
                <Send size={15} color="#fff" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
